import { Component, Inject, Injector } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { TCEmbeddedModeService } from '@cloud-twin-v2/cloud-twin-shared/core/embedded-mode.service';
import { TranslateScopes } from '@cloud-twin-v2/cloud-twin-shared/core/translate-scopes';
import {
  DateRangeDto,
  ElementTypeCategoriesDto,
} from '@cloud-twin-v2/generated/simulation.generated.client';
import { WidgetSettingsToken } from '@common-modules/dynamic-layout/dynamic-layout-external-settings';
import { SetValueAction } from '@common-modules/dynamic-layout/state/generic/generic.actions';
import { GetValueSelector } from '@common-modules/dynamic-layout/state/generic/generic.selectors';
import { BaseDynamicWidgetComponent } from '@common-modules/redux/components/base-dynamic-widget.component';
import { StateWidgetSettings } from '@common-modules/redux/models/state-widget-settings';
import { ReduxStateService } from '@common-modules/redux/redux-state.service';
import { MapTopbarButtonsConfiguration } from '@common-modules/wlm-map/components/map-top-bar-buttons/map-top-bar-buttons.component';
import { MapSidePanelSettings } from '@common-modules/wlm-map/components/side-panel/map-side-panel-settings';
import { LayerNameSpace } from '@common-modules/wlm-map/models/layer-namespace';
import { MapStep } from '@common-modules/wlm-map/models/map-step';
import { Observable, ReplaySubject } from 'rxjs';
import { filter, startWith } from 'rxjs/operators';
import { CtTopBarButtonsComponent } from '../../../../../cloud-twin/src/app/ct-top-bar-buttons/ct-top-bar-buttons.component';
import { MasterOperatorSimulateFormSidepanelComponent } from '../master-operator-simulate-form-sidepanel/master-operator-simulate-form-sidepanel.component';
import { MasterOperatorModule } from '../master-operator.module';
import { MasterOperatorConnectItemsSettings } from '../models/master-operator-connect-settings';
import { MasterOperatorFields } from '../models/master-operator-fields.enum';
import { ScenariosMetadata } from '../models/scenarios-metadata';
import { MasterOperatorService } from '../services/master-operator.service';
import { ScenarioInfoService } from '../services/scenario-info.service';

const COMPONENT_SELECTOR = 'tc-master-operator-map-widget';

@UntilDestroy()
@Component({
  selector: COMPONENT_SELECTOR,
  templateUrl: './master-operator-map-widget.component.html',
  styleUrls: ['./master-operator-map-widget.component.scss'],
  providers: [ReduxStateService],
  standalone: false,
})
export class MasterOperatorMapWidgetComponent extends BaseDynamicWidgetComponent {
  widgetInstanceKey: string;
  parentWidgetInstanceKey: string;
  namespaces: LayerNameSpace[];
  mapDateRange: DateRangeDto;
  sidePanelSettings: MapSidePanelSettings<any>;

  private _tooltipMetadata: Map<string, ElementTypeCategoriesDto[]>;
  public get tooltipMetadata(): Map<string, ElementTypeCategoriesDto[]> {
    return this._tooltipMetadata;
  }
  public set tooltipMetadata(value: Map<string, ElementTypeCategoriesDto[]>) {
    this._tooltipMetadata = value;
    this._tooltipMetadataChanged$.next(value);
  }

  private _selectedStep: MapStep;
  public get selectedStep(): MapStep {
    return this._selectedStep;
  }
  public set selectedStep(value: MapStep) {
    this._selectedStep = value;
    this._stepChanged$.next(value);
  }

  _stepChanged$ = new ReplaySubject<MapStep>();
  stepChanged$: Observable<MapStep> = this._stepChanged$.asObservable();
  _tooltipMetadataChanged$ = new ReplaySubject<Map<string, ElementTypeCategoriesDto[]>>();
  tooltipMetadataChanged$: Observable<Map<string, ElementTypeCategoriesDto[]>> =
    this._tooltipMetadataChanged$.asObservable();
  _connectSettingsChanged$ = new ReplaySubject<MasterOperatorConnectItemsSettings>();
  connectSettingsChanged$: Observable<MasterOperatorConnectItemsSettings> =
    this._connectSettingsChanged$.asObservable();

  topBarConfiguration: MapTopbarButtonsConfiguration;

  private _connectSettings: MasterOperatorConnectItemsSettings;
  get connectSettings(): MasterOperatorConnectItemsSettings {
    return this._connectSettings;
  }
  set connectSettings(value: MasterOperatorConnectItemsSettings) {
    this._connectSettings = value;
    if (value) {
      this.loadMapRange();
      this._connectSettingsChanged$.next(value);
    }
  }

  readonly T_SCOPE = `${TranslateScopes.MasterOperator}.${COMPONENT_SELECTOR}`;
  singleId: string;
  readonly isEmbedded$: Observable<boolean>;

  constructor(
    injector: Injector,
    @Inject(WidgetSettingsToken) widgetSettings: StateWidgetSettings,
    private _scenarioInfoService: ScenarioInfoService,
    private _masterOperatorService: MasterOperatorService,
    private readonly _embeddedModeService: TCEmbeddedModeService
  ) {
    super(injector, widgetSettings);
    this.widgetInstanceKey = widgetSettings.widgetInstanceKey;
    this.parentWidgetInstanceKey = widgetSettings.params.parentWidgetInstanceKey;
    this.connectSettings = widgetSettings.params.connect;
    this.buildSidePanelSettings();
    this.buildTopBarConfiguration();
    this.singleId = widgetSettings.params.singleId;
    this.isEmbedded$ = this._embeddedModeService.isEmbedded$;
  }

  onWidgetInit(): void {
    //TODO subscribe to value selector acction and filter by singleId
    this.getConnectSettings$().subscribe((connect: MasterOperatorConnectItemsSettings) => {
      this.connectSettings = connect;
    });

    this._state
      .select<ScenariosMetadata>(
        new GetValueSelector({
          fieldName: MasterOperatorFields.AttributesMetadata,
        })
      )
      .pipe(
        untilDestroyed(this),
        filter((data) => {
          return !!data && data.singleId == this.parentWidgetInstanceKey;
        })
      )
      .subscribe((value: ScenariosMetadata) => {
        this.tooltipMetadata = value.metadata;
      });
  }

  get componentName(): string {
    return 'MasterOperatorMapWidgetComponent';
  }

  onSelectedLayerChanged(layer) {
    this._state.dispatch(
      new SetValueAction({
        fieldName: MasterOperatorFields.Layer,
        value: layer,
      })
    );
  }

  onTimeChangedChanged(step: MapStep) {
    this._state.dispatch(
      new SetValueAction({
        fieldName: MasterOperatorFields.MapStep,
        value: step,
      })
    );

    this.selectedStep = step;
  }

  private getConnectSettings$(): Observable<MasterOperatorConnectItemsSettings> {
    return this._state
      .select<MasterOperatorConnectItemsSettings>(
        new GetValueSelector({
          fieldName: MasterOperatorFields.ConnectSettings,
        })
      )
      .pipe(
        untilDestroyed(this)
        /*filter((data) => {
          return !!data && data.singleId == this.itemSettings.params.singleId;
        })*/
      );
  }

  private loadMapRange() {
    this._scenarioInfoService
      .getDateRangesByMultiplesScenarios(this.connectSettings.items)
      .subscribe({
        next: (dates) => {
          this.mapDateRange = dates;
          this.namespaces = this.connectSettings.items.map(
            (x) =>
              new LayerNameSpace({
                namespace: `${x.snapshotId}-${x.scenarioId}`,
                name: this._masterOperatorService.instanceReadableLabel(x),
              })
          );
        },
        error: (error) => {
          this.mapDateRange = new DateRangeDto({ startDate: new Date(), endDate: new Date() });
          this.namespaces = this.connectSettings.items.map(
            (x) =>
              new LayerNameSpace({
                namespace: `${x.snapshotId}-${x.scenarioId}`,
                name: this._masterOperatorService.instanceReadableLabel(x),
              })
          );
        },
      });
  }

  private buildSidePanelSettings(): void {
    const connectSettings$ = this.getConnectSettings$().pipe(startWith(this.connectSettings));

    this.sidePanelSettings = {
      items: [
        {
          component: MasterOperatorSimulateFormSidepanelComponent,
          menuIcon: 'play_arrow',
          id: 'simulate',
          titleKey: `${this.T_SCOPE}.side-panel.labels.simulation`,
          featureInjector: MasterOperatorModule.injector,
          // Pass the configured stateService so the sidepanel can communicate with the outside.
          params: () => ({
            connectSettings$,
          }),
        },
      ],
    };
  }

  private buildTopBarConfiguration(): void {
    this.topBarConfiguration = new MapTopbarButtonsConfiguration({
      buttonsComponent: CtTopBarButtonsComponent,
      params: {
        attributesMetadataChanged$: this.tooltipMetadataChanged$,
        connectSettingsChanged$: this.connectSettingsChanged$,
        stepChanged$: this.stepChanged$,
      },
      featureInjector: MasterOperatorModule.injector,
    });
  }
}
